import { Badge, type BadgeProps } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

type BadgeVariant = NonNullable<BadgeProps['variant']>;

/**
 * Canonical mapping from a domain status string to a shadcn Badge variant.
 *
 * When a feature needs a status pill, look up the variant here instead of
 * hard-coding `<Badge variant="success">…</Badge>` inline. New statuses go in
 * this map; per-feature overrides go through the `variantMap` prop.
 */
export const DEFAULT_STATUS_VARIANTS: Record<string, BadgeVariant> = {
  // Invoices
  paid: 'success',
  partial: 'warning',
  open: 'info',
  pending: 'info',
  sent: 'info',
  overdue: 'danger',
  draft: 'secondary',
  void: 'outline',
  cancelled: 'outline',

  // Quotes / orders
  accepted: 'success',
  rejected: 'danger',
  expired: 'outline',
  approved: 'success',

  // Tickets / work orders / generic
  active: 'success',
  inactive: 'secondary',
  closed: 'secondary',
  resolved: 'success',
  blocked: 'danger',
  in_progress: 'info',
  in_review: 'warning',
  archived: 'outline',
};

const DEFAULT_LABELS: Record<string, string> = {
  in_progress: 'In progress',
  in_review: 'In review',
};

export interface StatusBadgeProps {
  /** Domain status key (case-insensitive, underscores allowed). */
  status: string | null | undefined;
  /** Override or extend the default status → variant map for one call site. */
  variantMap?: Record<string, BadgeVariant>;
  /** Override the displayed text (defaults to a Title Case version of `status`). */
  label?: string;
  /** Variant used when `status` isn't in any map. */
  fallbackVariant?: BadgeVariant;
  className?: string;
}

function toTitleCase(s: string): string {
  return s
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function StatusBadge({
  status,
  variantMap,
  label,
  fallbackVariant = 'secondary',
  className,
}: StatusBadgeProps) {
  if (!status) {
    return (
      <Badge variant="outline" className={cn('opacity-60', className)}>
        —
      </Badge>
    );
  }
  const key = status.toLowerCase().trim();
  const variant = variantMap?.[key] ?? DEFAULT_STATUS_VARIANTS[key] ?? fallbackVariant;
  const text = label ?? DEFAULT_LABELS[key] ?? toTitleCase(key);
  return (
    <Badge variant={variant} className={className}>
      {text}
    </Badge>
  );
}
